export function getVenmoAmount(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error("A valid quote total is required for Venmo.");
  }
  return amount.toFixed(2);
}

function release(instances) {
  return Promise.all(
    instances.map((instance) => {
      if (!instance || typeof instance.teardown !== "function") return null;
      return Promise.resolve().then(() => instance.teardown()).catch(() => {});
    })
  );
}

export async function prepareVenmoPayment(clientToken, quoteTotal, sandboxTest = false) {
  const amount = getVenmoAmount(quoteTotal);
  const sdk = window.braintree;
  if (!sdk?.client || (!sandboxTest && !sdk?.venmo) || !sdk?.dataCollector) {
    throw new Error("Venmo is unavailable in this browser.");
  }
  let client;
  let collector;
  let venmo;
  try {
    client = await sdk.client.create({ authorization: clientToken });
    collector = await sdk.dataCollector.create({ client, paypal: true });
    if (!sandboxTest) venmo = await sdk.venmo.create({
      client,
      paymentMethodUsage: "single_use",
      totalAmount: amount,
      allowDesktop: true,
      // Keep the quote and customer data in this tab during the app switch.
      allowNewBrowserTab: false,
    });
    if ((!sandboxTest && !venmo.isBrowserSupported()) || !collector.deviceData) {
      throw new Error("Venmo is unavailable in this browser.");
    }
    let closed = false;
    return {
      amount,
      sandboxTest,
      async tokenize() {
        if (closed) throw new Error("Venmo authorization has expired.");
        const payload = sandboxTest
          ? { nonce: "fake-venmo-account-nonce", type: "VenmoAccount" }
          : await venmo.tokenize();
        if (closed) throw new Error("Venmo authorization has expired.");
        if (!payload.nonce) throw new Error("Venmo authorization was not completed.");
        return {
          ...payload,
          deviceData: collector.deviceData,
          venmoAmount: amount,
        };
      },
      teardown() {
        if (closed) return Promise.resolve();
        closed = true;
        return release([venmo, collector, client]);
      },
    };
  } catch (error) {
    await release([venmo, collector, client]);
    throw error;
  }
}

export function isVenmoCancellation(error) {
  return ["VENMO_CANCELED", "VENMO_APP_CANCELED"].includes(error?.code);
}


export function getVenmoPaymentErrorMessage(error, stage = "authorization") {
  const fallback = stage === "authorization" ? "VENMO_AUTHORIZATION_FAILED" : "VENMO_ISSUANCE_FAILED";
  const code = typeof error?.code === "string" && /^[A-Z][A-Z0-9_]{0,63}$/.test(error.code)
    ? error.code : fallback;
  return "Venmo payment could not be completed. Please try again or select another payment method. Code: " + code;
}
